import { useEffect, useMemo, useRef, useState } from "react";
import { ChatStructuredReply, parseStructuredReply } from "./ChatStructuredReply";

const asList = (value) => {
  if (Array.isArray(value)) return value.filter((item) => item != null && item !== "");
  if (value == null || value === "") return [];
  return [value];
};

const optionLetter = (index) => String.fromCharCode(65 + index);

const replyToLines = (text) => {
  const data = parseStructuredReply(text);
  if (!data || typeof data !== "object") {
    return String(text || "").split("\n");
  }
  if (Array.isArray(data.questions) && data.questions.length) {
    return data.questions.flatMap((question, index) => {
      const options = asList(question.options).map((option, optionIndex) => {
        const label = typeof option === "string" ? option : option.text || option.label || "";
        return `${optionLetter(optionIndex)}. ${label}`;
      });
      return [
        `Question ${index + 1}`,
        question.stem || question.questionLine1 || question.question || "",
        ...options,
        question.answer || question.correctAnswer
          ? `Answer: ${question.answer || question.correctAnswer}`
          : "",
        "",
      ];
    });
  }
  if (data.lookup === "states" || data.lookup === "standards" || data.lookup === "create-setup" || data.lookup === "create-question" || data.lookup === "questions" || data.lookup === "question-summary") {
    const states = asList(data.states).length
      ? asList(data.states)
      : asList(data.versions).length
        ? asList(data.versions)
        : asList(data.availableStates);
    return [
      data.message || "",
      data.needed?.length ? `Still needed: ${data.needed.join(", ")}` : "",
      data.grade || data.subject
        ? [data.grade ? `Grade ${data.grade}` : "", data.subject, data.strand].filter(Boolean).join(" · ")
        : "",
      ...states.map(
        (row) =>
          `${row.versionName || row.state || "Catalog"}${row.framework ? ` · ${row.framework}` : ""} · ${row.standardCount || 0} standards`
      ),
      ...asList(data.standards).flatMap((standard) => [standard.code || "Standard", standard.description || "", ""]),
      ...asList(data.topics).map((topic) => topic.topicName || topic.name || "Topic"),
      ...asList(data.questions).map((question, index) => `Question ${index + 1}: ${question.stem || question.questionLine1 || ""}`),
    ];
  }
  if (Array.isArray(data.standards)) {
    const head = [data.framework, data.grade, data.subject, data.strand].filter(Boolean).join(" · ");
    return [
      head,
      ...data.standards.flatMap((standard) => [
        standard.code || "Standard",
        standard.description || "",
        "",
      ]),
    ];
  }
  if (data.imageUrl || data.prompt) {
    return [
      [data.grade, data.subject].filter(Boolean).join(" · ") || "Generated image",
      data.altText || data.prompt || "Here is your illustration.",
    ];
  }
  if (Array.isArray(data.topics)) {
    return data.topics.map((topic) => `${topic.ccssCode ? `${topic.ccssCode} — ` : ""}${topic.name || ""}`);
  }
  return String(text || "").split("\n");
};

const TypeLines = ({ lines, onDone }) => {
  const [lineIndex, setLineIndex] = useState(0);
  const [typed, setTyped] = useState("");
  const caretRef = useRef(null);
  const line = lines[lineIndex] ?? "";
  const finished = lineIndex >= lines.length;

  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  useEffect(() => {
    if (finished) {
      onDoneRef.current?.();
      return undefined;
    }
    if (typed.length < line.length) {
      const timer = setTimeout(() => {
        const step = line.length > 80 ? 3 : 1;
        setTyped(line.slice(0, typed.length + step));
      }, 14);
      return () => clearTimeout(timer);
    }
    const timer = setTimeout(() => {
      setLineIndex((value) => value + 1);
      setTyped("");
    }, 90);
    return () => clearTimeout(timer);
  }, [finished, line, typed]);

  useEffect(() => {
    caretRef.current?.scrollIntoView({ block: "nearest" });
  }, [typed, lineIndex]);

  return (
    <div className="ask-write">
      {lines.slice(0, lineIndex).map((item, index) => (
        <div key={index}>{item || "\u00a0"}</div>
      ))}
      {!finished ? (
        <div>
          {typed}
          <span className="ask-caret" ref={caretRef}>
            |
          </span>
        </div>
      ) : null}
    </div>
  );
};

export function WritingReply({
  text,
  instant = false,
  onChoice,
  onSaveStandards,
  onCancelStandards,
  onSaveQuestions,
  onCancelQuestions,
  onAddVisuals,
  onSkipVisuals,
}) {
  const lines = useMemo(() => replyToLines(text), [text]);
  const structured = parseStructuredReply(text);
  const [done, setDone] = useState(instant || lines.join("").length < 8);

  useEffect(() => {
    setDone(instant || lines.join("").length < 8);
  }, [text, instant, lines]);

  if (done) {
    return (
      <ChatStructuredReply
        text={text}
        onChoice={onChoice}
        onSaveStandards={onSaveStandards}
        onCancelStandards={onCancelStandards}
        onSaveQuestions={onSaveQuestions}
        onCancelQuestions={onCancelQuestions}
        onAddVisuals={onAddVisuals}
        onSkipVisuals={onSkipVisuals}
        fallback={<div className="ask-write">{text}</div>}
      />
    );
  }

  return (
    <>
      <TypeLines lines={lines} onDone={() => setDone(true)} />
      {structured?.imageUrl ? (
        <p className="ask-loading-copy">Finishing the illustration…</p>
      ) : null}
    </>
  );
}
